<template>
  <div class="record-language-editor">
    <v-select
      v-model="language"
      :items="languages"
      :label="$t('Entry language')"
      :disabled="disabled || saving"
      outlined
      dense
      hide-details
      class="mb-2"
    >
      <template v-slot:selection="{ item }">
        <img :src="item.flag" alt="" class="entry-language-flag">
        <span>{{ item.text }}</span>
      </template>
      <template v-slot:item="{ item }">
        <img :src="item.flag" alt="" class="entry-language-flag">
        <span>{{ item.text }}</span>
      </template>
    </v-select>
    <p class="text-caption mb-3">{{ $t('Changes only the text version of this entry, not the app language.') }}</p>
    <v-progress-linear v-if="loading" indeterminate class="mb-3" />
    <v-alert v-if="error" dense outlined type="error" role="alert">
      {{ error }}
      <v-btn v-if="!loaded" small text @click="load">{{ $t('Retry') }}</v-btn>
      <v-btn v-else small text @click="load(true)">{{ $t('Reload language versions') }}</v-btn>
    </v-alert>
    <div v-if="language === 'es-ES'" class="text-caption mb-3" role="status">
      {{ $t(!hasSpanish ? 'No Spanish version yet. Enter a translation below; the public page uses the original until it is saved.' : stale ? 'The original has changed. Review the Spanish version before publishing it again.' : 'You are editing the Spanish version. The original is kept separately.') }}
    </div>
    <div :lang="language === 'es-ES' ? 'es-ES' : null">
      <v-text-field v-model="title" :disabled="fieldsDisabled" outlined dense :label="$t('Title')" :error="titleError" />
      <div v-if="language === 'original'" class="d-flex align-center mb-1">
        <AiTextActions :value="description" :original-text="originalText" @update:originalText="$emit('update:originalText', $event)" @input="$emit('update:description', $event)" />
      </div>
      <wysiwyg v-if="!fieldsDisabled && language === 'original'" key="original" :html="description" :error="descriptionError" @html="$emit('update:description', $event)" />
      <wysiwyg v-else-if="!fieldsDisabled" key="es-ES" v-model="spanish.alternative_text" :error="descriptionError" />
    </div>
    <template v-if="language === 'es-ES' && loaded">
      <v-checkbox v-if="stale || reviewRequired" v-model="reviewed" :disabled="disabled || saving" hide-details :label="$t('I have reviewed the Spanish text against the current original.')" />
      <v-btn class="mt-3" small outlined color="primary" :disabled="disabled || !dirty || originalChanged" :loading="saving" @click="saveFromButton">
        {{ $t('Save Spanish version') }}
      </v-btn>
      <p v-if="originalChanged" class="text-caption mt-2">{{ $t('Use Update to save the original and Spanish changes together.') }}</p>
      <p v-if="success" class="text-caption mt-2" role="status">{{ success }}</p>
    </template>
  </div>
</template>
<script>
import axios from 'axios'
import { Auth } from 'aws-amplify'
import AiTextActions from '@/components/AiTextActions.vue'
import englishFlag from '@/assets/images/flags/us.svg'
import spanishFlag from '@/assets/images/flags/es.svg'

const blank = () => ({ name: '', alternative_text: '' })
const same = (a, b) => a.name === b.name && a.alternative_text === b.alternative_text
export default {
  components: { AiTextActions },
  props: {
    recordId: { type: [Number, String], required: true },
    name: { type: String, default: '' },
    description: { type: String, default: '' },
    originalText: { type: String, default: null },
    disabled: Boolean,
    titleError: Boolean,
    descriptionError: Boolean,
  },
  data: () => ({ language: 'original', spanish: blank(), baseline: blank(), snapshot: null, loaded: false, loading: false, saving: false, reviewed: false, reviewRequired: false, error: '', success: '', request: 0 }),
  computed: {
    languages() { return [{ value: 'original', text: this.$t('Original'), flag: englishFlag }, { value: 'es-ES', text: 'Español', flag: spanishFlag }] },
    currentOriginal() { return { name: this.name, alternative_text: this.description } },
    originalChanged() { return !!this.snapshot && !same(this.currentOriginal, this.snapshot.original) },
    hasSpanish() { return !!(this.snapshot && this.snapshot.spanish.fields) },
    stale() { return this.hasSpanish && (this.originalChanged || this.snapshot.spanish.status === 'stale') },
    dirty() { return this.loaded && (!same(this.spanish, this.baseline) || this.reviewed) },
    fieldsDisabled() { return this.disabled || this.saving || (this.language === 'es-ES' && !this.loaded) },
    title: { get() { return this.language === 'original' ? this.name : this.spanish.name }, set(value) { if (this.language === 'original') this.$emit('update:name', value); else this.spanish.name = value } },
  },
  watch: {
    recordId: { immediate: true, handler() { this.language = 'original'; this.loaded = false; this.snapshot = null; this.spanish = blank(); this.baseline = blank(); this.reviewed = false; void this.load() } },
    dirty(value) { this.$emit('dirty-change', value) },
    saving(value) { this.$emit('saving-change', value) },
    originalChanged() { this.reviewed = false },
  },
  beforeDestroy() { this.request += 1 },
  methods: {
    async headers() { const session = await Auth.currentSession(); return { Authorization: session.getAccessToken().getJwtToken() } },
    message(error) { return this.$t((error.response && error.response.data && error.response.data.message) || 'Language versions could not be saved or loaded. Please try again.') },
    async load(keepDraft = false) {
      const preserve = keepDraft === true && this.dirty
      const request = ++this.request
      this.loading = true; this.error = ''; this.success = ''
      try {
        const { data } = await axios.get(`${process.env.VUE_APP_ROOT_API}/records/${this.recordId}/translations`, { headers: await this.headers() })
        if (request !== this.request) return
        this.snapshot = data
        this.baseline = { ...(data.spanish.fields || blank()) }
        if (!preserve) this.spanish = { ...this.baseline }
        this.reviewRequired = preserve; this.reviewed = false; this.loaded = true
      } catch (error) { if (request === this.request) this.error = this.message(error) } finally { if (request === this.request) this.loading = false }
    },
    prepareSave() {
      if (!this.dirty) return true
      if (!this.spanish.name.trim() || (this.description.trim() && !this.spanish.alternative_text.replace(/<[^>]*>/g, '').trim())) {
        this.error = this.$t('Enter a valid Spanish title and description.'); this.language = 'es-ES'; return false
      }
      if ((this.stale || this.reviewRequired) && !this.reviewed) { this.error = this.$t('Review the Spanish text and check the confirmation before saving.'); this.language = 'es-ES'; return false }
      return true
    },
    async save() {
      if (!this.dirty) return
      if (!this.prepareSave()) throw new Error(this.error)
      this.saving = true; this.error = ''; this.success = ''
      try {
        const headers = await this.headers()
        const url = `${process.env.VUE_APP_ROOT_API}/records/${this.recordId}/translations`
        const { data: latest } = await axios.get(url, { headers })
        if (!same(latest.original, this.currentOriginal)) throw new Error(this.$t('The original text changed. Reload the language versions before saving.'))
        const { data } = await axios.patch(url, { locale: 'es-ES', source_hash: latest.sourceHash, revision: this.snapshot.spanish.revision, fields: { ...this.spanish } }, { headers })
        this.snapshot = data; this.spanish = { ...data.spanish.fields }; this.baseline = { ...this.spanish }; this.reviewed = false; this.reviewRequired = false
        this.success = this.$t('Spanish version saved. No AI credits used.'); this.$emit('saved')
      } catch (error) { this.error = error.response ? this.message(error) : (error.message || this.message(error)); throw error } finally { this.saving = false }
    },
    async saveFromButton() { try { await this.save() } catch (_) { /* Keep the draft and error visible for retry. */ } },
  },
}
</script>
<style scoped>
.entry-language-flag { width: 24px; height: 16px; object-fit: cover; border-radius: 2px; margin-right: 10px; }
.record-language-editor { min-width: 0; }
.record-language-editor ::v-deep .v-select__selection { max-width: 100%; }
.record-language-editor ::v-deep .v-label { white-space: normal; line-height: 1.4; }
.record-language-editor ::v-deep .editr--toolbar { flex-wrap: wrap; height: auto; min-height: 32px; }
.record-language-editor ::v-deep .editr--toolbar a { flex: 0 0 32px; width: 32px; }
.record-language-editor ::v-deep .editr--toolbar .dashboard { top: 100%; }
.record-language-editor ::v-deep .editr--content { overflow-wrap: anywhere; }
</style>
